import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * Keeps compare-at / sale price order aligned with the Price block setting after section updates.
 * @param {Element} container
 * @param {boolean} showSaleFirst
 */
function applySalePriceOrder(container, showSaleFirst) {
  const children = Array.from(container.children);
  const compareEl = children.find((el) => el.querySelector?.(':scope .compare-at-price'));
  const priceEl = children.find((el) => {
    if (el === compareEl) return false;
    if (el.classList.contains('unit-price') || el.querySelector?.(':scope .unit-price')) return false;
    return el.classList.contains('price') || Boolean(el.querySelector?.(':scope .price'));
  });
  if (!compareEl || !priceEl) return;

  if (showSaleFirst) {
    container.insertBefore(priceEl, compareEl);
  } else {
    container.insertBefore(compareEl, priceEl);
  }
}

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    const incomingProductId = event.detail.data.newProduct?.id ?? event.detail.data.productId ?? null;
    if (incomingProductId) {
      this.dataset.productId = String(incomingProductId);
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const fragment = event.detail.data.html;
    let newProductPrice = fragment.querySelector(`product-price[data-block-id="£{this.dataset.blockId}"]`);
    if (!newProductPrice) {
      newProductPrice =
        fragment.querySelector('.product-details product-price') ?? fragment.querySelector('product-price');
    }
    if (!newProductPrice) return;

    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');
    if (newPrice && currentPrice) {
      currentPrice.replaceWith(newPrice);
    } else if (newPrice) {
      this.prepend(newPrice);
    }

    const priceContainer = this.querySelector('[ref="priceContainer"]');
    if (priceContainer) {
      applySalePriceOrder(priceContainer, this.dataset.showSalePriceFirst !== 'false');
    }

    // Update volume pricing note
    const currentNote = this.querySelector('.volume-pricing-note');
    const newNote = newProductPrice.querySelector('.volume-pricing-note');

    if (!newNote) {
      currentNote?.remove();
    } else if (!currentNote) {
      this.querySelector('[ref="priceContainer"]')?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      currentNote.replaceWith(newNote);
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
